import type {
    SourceDocument,
    Opportunity,
    Extraction,
    DocumentListResponse,
    DocumentFullResponse,
    Department,
    DepartmentTreeNode,
    Position,
    Employee,
    UserListRead
} from '../models';
import type { BriefData } from '../types/pipeline';
import type {
    AgentSummary,
    AgentDetail,
    AgentVersionSummary,
    AgentVersionDetail,
    AgentVersionCreate,
    ExecutionDetail,
    ExecutionListResponse,
    SkillSummary,
    TestInvocationResponse,
} from '../types/agent-studio';

// VITE_API_URL is read from .env at repo root; fallback is dev default.
// For production deployment, set VITE_API_URL in the deployment env.
export const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';

/**
 * Strip Python implementation details from backend error strings:
 * - UUID tokens (field_name=<uuid>)
 * - Python field=value patterns (e.g. execution_mode='manual', assigned_agent_id=None)
 * - Python !r repr artifacts
 * Falls back to a generic Russian message if the result is too short.
 */
function sanitizePythonRepr(raw: string): string {
    let s = raw
        // Strip UUIDs
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '')
        // Strip Python field=value tokens: word='value', word=None, word=True, word=123
        .replace(/\b\w+=(?:'[^']*'|"[^"]*"|None|True|False|-?\d+(?:\.\d+)?)/g, '')
        // Collapse multiple spaces / orphaned commas / leading colon
        .replace(/,\s*,/g, '')
        .replace(/:\s*[,.]?\s*$/m, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
    return s.length >= 5 ? s : 'Действие не может быть выполнено в текущем состоянии задачи.';
}

function mapExtraction(raw: any): Extraction {
    const modSigs: any = raw.modification_signals ?? {};
    const procSigs: any = raw.process_signals ?? {};
    const ingrSigs: any = raw.ingredient_signals ?? {};

    const join = (v: unknown) =>
        Array.isArray(v) ? v.join(', ') : typeof v === 'string' ? v : '';

    return {
        id: raw.id,
        sourceDocumentId: raw.source_id,
        targetProblem: raw.target_problem ?? '',
        targetArea: '',
        claimedBenefits: join(raw.claimed_benefits),
        materialSignals: [modSigs.modification_type, join(modSigs.reagents)].filter(Boolean).join(' | '),
        processSignals: [join(procSigs.steps), join(procSigs.equipment)].filter(Boolean).join(' | '),
        formFactor: modSigs.form_factor ?? procSigs.form_factor ?? '',
        ingredientSignals: [ingrSigs.base, join(ingrSigs.additives)].filter(Boolean).join(', '),
        regulatorySensitivity: raw.regulatory_sensitivity ?? '',
        evidenceSnippets: Array.isArray(raw.evidence_snippets) ? raw.evidence_snippets : [],
        relevanceScore: Number(raw.relevance_score) || 0,
        confidenceScore: Number(raw.confidence_score) || 0,
        extractionStatus: 'completed',
        createdAt: raw.created_at ?? '',
        updatedAt: raw.updated_at ?? raw.created_at ?? '',
    };
}

function mapSource(raw: any): SourceDocument {
    return {
        id: raw.id,
        title: raw.title,
        title_ru: raw.title_ru ?? undefined,
        country: raw.country ?? undefined,
        sourceType: raw.source_type,
        sourceSystem: raw.source_system,
        publicationNumber: raw.publication_number ?? undefined,
        sourceUrl: raw.source_url ?? '',
        assigneeOrAuthor: raw.assignee ?? '',
        publicationDate: raw.publication_date ?? '',
        rawText: raw.raw_text ?? '',
        status: raw.status,
        pdfUrl: raw.pdf_url ?? undefined,
        metadata: raw.metadata ?? {},
        createdAt: raw.created_at ?? '',
        updatedAt: raw.updated_at ?? raw.created_at ?? '',
    };
}

export class ApiClient {
    private getToken() {
        return localStorage.getItem('bentonit_token');
    }

    private clearToken() {
        localStorage.removeItem('bentonit_token');
    }

    private async baseRequest<T>(baseUrl: string, endpoint: string, options: RequestInit & { silent?: boolean; skipAuthRedirect?: boolean } = {}): Promise<T> {
        let headers: Record<string, string> = {
            ...(options.headers as Record<string, string> || {})
        };

        if (!headers['Content-Type'] && options.method && options.method !== 'GET' && !(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        const token = this.getToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        try {
            const url = `${baseUrl}${endpoint}`;
            const response = await fetch(url, { ...options, headers });

            // An expired session anywhere in the app bounces to /login. The login
            // request itself must opt out: a wrong password is also a 401, and
            // redirecting on it reloads the page and discards the server's
            // message, so the form appears to do nothing at all.
            if (response.status === 401 && !options.skipAuthRedirect) {
                this.clearToken();
                window.location.href = '/login';
                throw new Error('Unauthorized - Please login again');
            }
            if (!response.ok) {
                let errorMessage = `Ошибка сервера, попробуйте позже`;
                let errorJson: any = null;
                try {
                    const text = await response.text();
                    errorJson = JSON.parse(text);
                } catch (e) {
                    // Ignore parse error
                }

                if (response.status === 403) {
                    const d = errorJson?.detail;
                    if (typeof d === 'string' && d.includes("['admin', 'owner']")) {
                        errorMessage = 'Только администратор или владелец может выполнить это действие.';
                    } else if (typeof d === 'string' && d.length > 0) {
                        errorMessage = 'У вас нет прав для этого действия. Обратитесь к администратору.';
                    } else {
                        errorMessage = 'У вас нет прав для этого действия. Обратитесь к администратору.';
                    }
                } else if (response.status === 422) {
                    if (errorJson && errorJson.detail && errorJson.detail.allowed_transitions) {
                        errorMessage = `Невозможный переход. Допустимые статусы: ${errorJson.detail.allowed_transitions.join(', ')}`;
                    } else if (errorJson && errorJson.detail && typeof errorJson.detail === 'string') {
                        errorMessage = sanitizePythonRepr(errorJson.detail);
                    } else if (errorJson && errorJson.detail && errorJson.detail.message) {
                        errorMessage = sanitizePythonRepr(String(errorJson.detail.message));
                    } else {
                        errorMessage = 'Действие не может быть выполнено. Проверьте данные и попробуйте снова.';
                    }
                } else if (response.status >= 500) {
                    errorMessage = 'Ошибка сервера, попробуйте позже';
                } else if (errorJson && errorJson.detail) {
                    errorMessage = typeof errorJson.detail === 'string' ? errorJson.detail : JSON.stringify(errorJson.detail);
                }

                const err: any = new Error(errorMessage);
                err.status = response.status;
                err.originalDetail = errorJson?.detail;
                throw err;
            }

            const text = await response.text();
            return (text ? JSON.parse(text) : undefined) as T;
        } catch (e: any) {
            console.error('API call failed:', e);
            if (!options.silent && e.status !== 404) {
                let msg = e.message || 'API Error';
                if (e.message.includes('Failed to fetch') || e.message.includes('NetworkError')) {
                    msg = 'Не удалось связаться с сервером, попробуйте снова';
                }
                window.dispatchEvent(new CustomEvent('api-error', { detail: msg }));
            }
            throw e;
        }
    }

    public async request<T>(endpoint: string, options: RequestInit & { silent?: boolean; skipAuthRedirect?: boolean } = {}): Promise<T> {
        return this.baseRequest<T>(`${API_BASE}/api/v1`, endpoint, options);
    }

    private async pipelineRequest<T>(endpoint: string, options: RequestInit & { silent?: boolean } = {}): Promise<T> {
        // Log for debugging
        if (!options.silent) {
            console.log(`[PipelineRequest] ${endpoint}`);
        }
        return this.baseRequest<T>(`${API_BASE}/api`, endpoint, options);
    }

    async login(email: string, password: string): Promise<{ access_token: string }> {
        // This platform's `POST /auth/login` takes a JSON body — see the
        // `LoginRequest` model in `backend/app/routers/auth.py`. The
        // form-encoded `username`/`password` this used to send was inherited
        // from the host application's OAuth2PasswordRequestForm route and had
        // no matching endpoint here, so every sign-in attempt was rejected with
        // a 422 before the password was ever checked.
        const res = await this.request<{ access_token: string }>('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
            skipAuthRedirect: true,
        });
        localStorage.setItem('bentonit_token', res.access_token);
        return res;
    }

    async getSources(filters?: {
        status?: string;
        source_type?: string;
        source_system?: string;
        search?: string;
        has_document?: boolean;
        limit?: number;
        offset?: number;
    }): Promise<{ items: SourceDocument[]; total: number }> {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.source_type) params.append('source_type', filters.source_type);
        if (filters?.source_system) params.append('source_system', filters.source_system);
        if (filters?.search) params.append('search', filters.search);
        if (filters?.has_document != null) params.append('has_document', String(filters.has_document));
        if (filters?.limit != null) params.append('limit', String(filters.limit));
        if (filters?.offset != null) params.append('offset', String(filters.offset));
        const qs = params.toString();
        const res = await this.request<{ items: any[], total: number }>(`/sources${qs ? '?' + qs : ''}`);
        return { items: (res.items ?? []).map(mapSource), total: res.total ?? 0 };
    }

    async getSource(id: string, silent: boolean = true): Promise<SourceDocument> {
        const res = await this.request<any>(`/sources/${id}`, { silent });
        const raw = res.source ?? res;
        return mapSource(raw);
    }

    async getSourceDetail(id: string, silent: boolean = true): Promise<any> {
        return this.request<any>(`/sources/${id}`, { silent });
    }

    async getDocuments(filters?: {
        page?: number;
        page_size?: number;
        document_type?: string;
        application_area?: string;
        status?: string;
        ijevan_compatibility?: string;
        search?: string;
        sort_by?: string;
        sort_order?: 'asc' | 'desc';
    }): Promise<DocumentListResponse> {
        const params = new URLSearchParams();
        if (filters?.page != null) params.append('page', String(filters.page));
        if (filters?.page_size != null) params.append('page_size', String(filters.page_size));
        if (filters?.document_type) params.append('document_type', filters.document_type);
        if (filters?.application_area) params.append('application_area', filters.application_area);
        if (filters?.status) params.append('status', filters.status);
        if (filters?.ijevan_compatibility) params.append('ijevan_compatibility', filters.ijevan_compatibility);
        if (filters?.search) params.append('search', filters.search);
        if (filters?.sort_by) params.append('sort_by', filters.sort_by);
        if (filters?.sort_order) params.append('sort_order', filters.sort_order);

        const qs = params.toString();
        return this.request<DocumentListResponse>(`/documents/${qs ? '?' + qs : ''}`);
    }

    async getDocumentFull(id: string, silent: boolean = true): Promise<DocumentFullResponse> {
        return this.request<DocumentFullResponse>(`/documents/${id}/full`, { silent });
    }

    async createSource(data: any): Promise<SourceDocument> {
        const raw = await this.request<any>('/sources', { method: 'POST', body: JSON.stringify(data) });
        return mapSource(raw);
    }

    async updateSourceStatus(id: string, status: string): Promise<void> {
        return this.request<void>(`/sources/${id}/status?status=${status}`, { method: 'PATCH', body: JSON.stringify({ status }) });
    }

    async getOpportunities(filters?: { status?: string, limit?: number, offset?: number }): Promise<{ items: Opportunity[], total: number }> {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.limit) params.append('limit', String(filters.limit));
        if (filters?.offset) params.append('offset', String(filters.offset));
        const qs = params.toString();
        return this.request<{ items: Opportunity[], total: number }>(`/opportunities${qs ? '?' + qs : ''}`);
    }

    async updateOpportunityStatus(id: string, status: string): Promise<void> {
        return this.request<void>(`/opportunities/${id}/status?status=${status}`, { method: 'PATCH', body: JSON.stringify({ status }) });
    }

    async createOpportunity(data: Opportunity): Promise<Opportunity> {
        return this.request<Opportunity>('/opportunities', { method: 'POST', body: JSON.stringify(data) });
    }

    // ── Agent Studio methods (SOW 4.1) ────────────────────────────────────────
    // All 10 endpoints match the locked contract in
    // docs/PHASE1_AGENT_STUDIO_UI_PLAN.md §5.

    async listAgents(): Promise<{ agents: AgentSummary[] }> {
        return this.request<{ agents: AgentSummary[] }>('/agents');
    }

    async getAgent(systemName: string): Promise<AgentDetail> {
        return this.request<AgentDetail>(`/agents/${systemName}`);
    }

    async listAgentVersions(systemName: string): Promise<{ versions: AgentVersionSummary[] }> {
        return this.request<{ versions: AgentVersionSummary[] }>(`/agents/${systemName}/versions`);
    }

    async getAgentVersion(systemName: string, versionId: string): Promise<AgentVersionDetail> {
        return this.request<AgentVersionDetail>(`/agents/${systemName}/versions/${versionId}`);
    }

    async createAgentVersion(systemName: string, payload: AgentVersionCreate): Promise<AgentVersionDetail> {
        return this.request<AgentVersionDetail>(`/agents/${systemName}/versions`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    async activateAgentVersion(systemName: string, versionId: string): Promise<AgentDetail> {
        return this.request<AgentDetail>(`/agents/${systemName}/versions/${versionId}/activate`, {
            method: 'POST',
        });
    }

    async testAgentVersion(
        systemName: string,
        versionId: string,
        fixtureName: string,
    ): Promise<TestInvocationResponse> {
        return this.request<TestInvocationResponse>(
            `/agents/${systemName}/versions/${versionId}/test`,
            { method: 'POST', body: JSON.stringify({ fixture_name: fixtureName }) },
        );
    }

    async listAgentExecutions(
        systemName: string,
        opts?: { limit?: number; cursor?: string },
    ): Promise<ExecutionListResponse> {
        const params = new URLSearchParams();
        if (opts?.limit) params.append('limit', String(opts.limit));
        if (opts?.cursor) params.append('before', opts.cursor);
        const qs = params.toString();
        return this.request<ExecutionListResponse>(
            `/agents/${systemName}/executions${qs ? `?${qs}` : ''}`,
        );
    }

    async getAgentExecution(systemName: string, executionId: string): Promise<ExecutionDetail> {
        return this.request<ExecutionDetail>(`/agents/${systemName}/executions/${executionId}`);
    }

    async listSkills(): Promise<{ skills: SkillSummary[] }> {
        return this.request<{ skills: SkillSummary[] }>('/skills');
    }

    // ── End Agent Studio methods ──────────────────────────────────────────────

    async getReviewTasks(status?: string, silent: boolean = true): Promise<any[]> {
        const qs = status && status !== 'All' ? `?status=${status.toLowerCase()}` : '';
        return this.request<any[]>(`/review/tasks${qs}`, { silent });
    }

    async getReviewTaskDetails(taskId: string, silent: boolean = true): Promise<any> {
        return this.request<any>(`/review/tasks/${taskId}`, { silent });
    }

    async claimReviewTask(taskId: string): Promise<any> {
        return this.request<any>(`/review/tasks/${taskId}/claim`, { method: 'POST' });
    }

    async decideReviewTask(taskId: string, payload: any): Promise<any> {
        return this.request<any>(`/review/tasks/${taskId}/decide`, { method: 'POST', body: JSON.stringify(payload) });
    }

    async getPendingReviewTaskCount(silent: boolean = true): Promise<{ count: number }> {
        return this.request<{ count: number }>('/review/tasks/pending-count', { silent });
    }

    async getScoutDashboard(silent: boolean = true): Promise<any> {
        return this.request<any>('/scout/dashboard', { silent });
    }

    async getScoutProfiles(silent: boolean = true): Promise<any[]> {
        return this.request<any[]>('/scout/profiles', { silent });
    }

    async runScoutProfile(id: string): Promise<any> {
        return this.request<any>(`/scout/profiles/${id}/run`, { method: 'POST' });
    }

    async deleteScoutProfile(id: string): Promise<void> {
        return this.request<void>(`/scout/profiles/${id}`, { method: 'DELETE' });
    }

    async createScoutProfile(data: any): Promise<any> {
        return this.request<any>('/scout/profiles', { method: 'POST', body: JSON.stringify(data) });
    }

    async updateScoutProfile(id: string, data: any): Promise<any> {
        return this.request<any>(`/scout/profiles/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    }

    async getProducts(): Promise<{ items: any[], total: number }> {
        return this.request<{ items: any[], total: number }>('/graph/products');
    }

    async getApplications(): Promise<any[]> {
        return this.request<any[]>('/graph/applications');
    }


    async getCapabilities(): Promise<any[]> {
        return this.request<any[]>('/capabilities').catch(() => []);
    }

    async saveCapability(data: any): Promise<any> {
        return this.request<any>('/capabilities', { method: 'POST', body: JSON.stringify(data) }).catch(() => data);
    }

    async getReviewsForSource(sourceId: string): Promise<{ items: any[], total: number }> {
        return this.request<{ items: any[], total: number }>(`/reviews?source_id=${sourceId}`, { silent: true }).catch(() => ({ items: [], total: 0 }));
    }

    async getExtractionBySourceId(sourceId: string): Promise<Extraction | null> {
        try {
            const res = await this.request<{ items: any[], total: number }>(
                `/extractions?source_id=${sourceId}&limit=1`,
                { silent: true }
            );
            if (!res.items || res.items.length === 0) return null;
            return mapExtraction(res.items[0]);
        } catch {
            return null;
        }
    }

    async analyzeSource(id: string): Promise<{ queued: boolean; source_id: string }> {
        return this.request<{ queued: boolean; source_id: string }>(
            `/agents/analyst/run/${id}`,
            { method: 'POST' }
        );
    }

    async saveExtraction(data: any): Promise<any> {
        return this.request<any>('/extractions', { method: 'POST', body: JSON.stringify(data) }).catch(() => data);
    }

    async createReview(data: {
        item_type: string;
        item_id: string;
        decision: string;
        rejection_reason?: string;
        comment?: string;
        quality_score?: number;
    }): Promise<any> {
        return this.request<any>('/reviews', { method: 'POST', body: JSON.stringify(data) });
    }

    async getReviews(): Promise<{ items: any[], total: number }> {
        return this.request<{ items: any[], total: number }>('/reviews').catch(() => ({ items: [], total: 0 }));
    }

    async uploadSourceFile(sourceId: string, file: File): Promise<SourceDocument> {
        const formData = new FormData();
        formData.append('file', file);
        const raw = await this.request<any>(`/sources/${sourceId}/upload`, {
            method: 'POST',
            body: formData,
        });
        return mapSource(raw);
    }

    async openSourceFile(sourceId: string, filename: string): Promise<void> {
        const token = this.getToken();
        const url = `${API_BASE}/api/v1/sources/${sourceId}/file`;
        const res = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`Failed to fetch file: ${res.status}`);
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        // PDFs open in a new tab; other formats download
        if (filename.toLowerCase().endsWith('.pdf')) {
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
        } else {
            a.download = filename;
        }
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    }

    async listProjects(status?: string): Promise<any[]> {
        const qs = status && status !== 'Все' ? `?status=${status.toLowerCase()}` : '';
        return this.request<any[]>(`/projects${qs}`);
    }

    async createProject(data: any): Promise<any> {
        return this.request<any>('/projects', { method: 'POST', body: JSON.stringify(data) });
    }

    async listProducts(filters?: any): Promise<{ items: any[], total: number }> {
        const params = new URLSearchParams();
        if (filters?.category) params.append('category', filters.category);
        if (filters?.search) params.append('search', filters.search);
        const qs = params.toString();
        return this.request<{ items: any[], total: number }>(`/products${qs ? '?' + qs : ''}`);
    }

    async getProduct(id: string): Promise<any> {
        return this.request<any>(`/products/${id}`);
    }

    async createProduct(data: any): Promise<any> {
        return this.request<any>('/products', { method: 'POST', body: JSON.stringify(data) });
    }

    async updateProduct(id: string, data: any): Promise<any> {
        return this.request<any>(`/products/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    }

    async deleteProduct(id: string): Promise<void> {
        return this.request<void>(`/products/${id}`, { method: 'DELETE' });
    }

    async getProductCategories(): Promise<string[]> {
        return this.request<string[]>('/products/categories');
    }

    async linkProductToProject(projectId: string, productId: string, data?: any): Promise<any> {
        return this.request<any>(`/projects/${projectId}/products/${productId}`, { method: 'POST', body: JSON.stringify(data || {}) });
    }

    async unlinkProductFromProject(projectId: string, productId: string): Promise<void> {
        return this.request<void>(`/projects/${projectId}/products/${productId}`, { method: 'DELETE' });
    }

    async getProjectWorkspace(projectId: string): Promise<any> {
        return this.request<any>(`/projects/${projectId}/workspace`);
    }

    async getProjectAgentRuns(projectId: string): Promise<any[]> {
        return this.request<any[]>(`/projects/${projectId}/agent-runs`);
    }

    async triggerAgentRun(projectId: string, patentId: string): Promise<{ run_id: string, status: string }> {
        return this.request<{ run_id: string, status: string }>(`/projects/${projectId}/agent-runs/trigger`, { method: 'POST', body: JSON.stringify({ patent_id: patentId }) });
    }

    async getAgentRun(runId: string): Promise<any> {
        return this.request<any>(`/agent-runs/${runId}`);
    }

    async getBrief(briefId: string): Promise<any> {
        return this.request<any>(`/briefs/${briefId}`);
    }

    // ── Project Types ─────────────────────────────────────────────────────
    async listProjectTypes(): Promise<any[]> {
        return this.request<any[]>('/project-types');
    }

    async getMasterPromptSchema(projectTypeId: string): Promise<any> {
        return this.request<any>(`/project-types/${projectTypeId}`);
    }

    async getMasterPromptSchemaByKey(key: string): Promise<any> {
        return this.request<any>(`/project-types/by-key/${key}`);
    }

    // ── Projects (extended) ───────────────────────────────────────────────
    async getProject(id: string): Promise<any> {
        return this.request<any>(`/projects/${id}`);
    }

    async createProjectWithMasterPrompt(data: {
        name_ru: string;
        name_en: string;
        project_type_id: string;
        raw_user_input: string;
    }): Promise<{ project: any; master_prompt: any }> {
        return this.request<{ project: any; master_prompt: any }>('/projects/with-master-prompt', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // ── Master Prompts ────────────────────────────────────────────────────
    async getMasterPrompt(id: string): Promise<any> {
        return this.request<any>(`/master-prompts/${id}`);
    }

    async updateMasterPrompt(id: string, data: { normalized_content: any; field_quality?: any }): Promise<any> {
        return this.request<any>(`/master-prompts/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    }

    async approveMasterPrompt(id: string): Promise<any> {
        return this.request<any>(`/master-prompts/${id}/approve`, { method: 'POST' });
    }

    async listProjectMasterPrompts(projectId: string): Promise<any[]> {
        return this.request<any[]>(`/projects/${projectId}/master-prompts`);
    }

    async renormalizeMasterPrompt(projectId: string, raw_user_input: string): Promise<any> {
        return this.request<any>(`/projects/${projectId}/master-prompts/renormalize`, {
            method: 'POST',
            body: JSON.stringify({ raw_user_input }),
        });
    }

    // ── Pipeline Orchestrator ───────────────────────────────────────────
    async runProjectPipeline(projectId: string): Promise<any> {
        return this.pipelineRequest<any>(`/projects/${projectId}/pipeline/run`, {
            method: 'POST',
            body: JSON.stringify({})
        });
    }

    async getPipelineRunStatus(runId: string): Promise<any> {
        return this.pipelineRequest<any>(`/pipeline/${runId}`);
    }

    async archiveProject(projectId: string): Promise<void> {
        return this.request<void>(`/projects/${projectId}/archive`, { method: 'PATCH' });
    }

    async getBriefData(runId: string): Promise<BriefData> {
        return this.pipelineRequest<BriefData>(`/pipeline/${runId}/brief-data`);
    }

    async listProjectPipelineRuns(projectId: string): Promise<any[]> {
        return this.pipelineRequest<any[]>(`/projects/${projectId}/pipeline/runs`);
    }

    async downloadBriefPdf(runId: string): Promise<void> {
        const token = this.getToken();
        const url = `${API_BASE}/api/pipeline/${runId}/brief.pdf`;
        const res = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
            if (res.status === 404) {
                throw new Error('PDF rendering is not yet implemented backend-side.');
            }
            throw new Error(`Failed to download PDF: ${res.status}`);
        }
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `brief_${runId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    }

    async listDepartments(includeInactive: boolean = false, parentDepartmentId?: string): Promise<Department[]> {
        const params = new URLSearchParams();
        if (includeInactive) params.set('include_inactive', 'true');
        if (parentDepartmentId) params.set('parent_department_id', parentDepartmentId);
        const qs = params.toString() ? `?${params.toString()}` : '';
        return this.request<Department[]>(`/departments${qs}`);
    }

    async getDepartmentTree(includeInactive: boolean = false): Promise<DepartmentTreeNode[]> {
        const qs = includeInactive ? '?include_inactive=true' : '';
        return this.request<DepartmentTreeNode[]>(`/departments/tree${qs}`);
    }

    async listPositions(departmentId?: string, includeInactive: boolean = false): Promise<Position[]> {
        const params = new URLSearchParams();
        if (departmentId) params.set('department_id', departmentId);
        if (includeInactive) params.set('include_inactive', 'true');
        const qs = params.toString() ? `?${params.toString()}` : '';
        return this.request<Position[]>(`/positions${qs}`);
    }

    async listEmployees(filters?: {
        departmentId?: string;
        positionId?: string;
        managerId?: string;
        includeInactive?: boolean;
        limit?: number;
        offset?: number;
    }): Promise<Employee[]> {
        const params = new URLSearchParams();
        if (filters?.departmentId) params.set('department_id', filters.departmentId);
        if (filters?.positionId) params.set('position_id', filters.positionId);
        if (filters?.managerId) params.set('manager_id', filters.managerId);
        if (filters?.includeInactive) params.set('include_inactive', 'true');
        if (filters?.limit) params.set('limit', String(filters.limit));
        if (filters?.offset) params.set('offset', String(filters.offset));
        const qs = params.toString() ? `?${params.toString()}` : '';
        return this.request<Employee[]>(`/employees${qs}`);
    }

    async createDepartment(payload: {
        name_ru: string;
        name_en: string;
        description_ru?: string | null;
        description_en?: string | null;
        parent_department_id?: string | null;
        display_order?: number;
    }): Promise<Department> {
        return this.request<Department>('/departments', {
            method: 'POST',
            body: JSON.stringify(payload),
            silent: true,
        });
    }

    async updateDepartment(id: string, payload: Partial<{
        name_ru: string;
        name_en: string;
        description_ru: string | null;
        description_en: string | null;
        parent_department_id: string | null;
        display_order: number;
    }>): Promise<Department> {
        return this.request<Department>(`/departments/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
            silent: true,
        });
    }

    async deactivateDepartment(id: string): Promise<Department> {
        return this.request<Department>(`/departments/${id}/deactivate`, {
            method: 'POST',
            silent: true,
        });
    }

    async reactivateDepartment(id: string): Promise<Department> {
        return this.request<Department>(`/departments/${id}/reactivate`, {
            method: 'POST',
            silent: true,
        });
    }

    async createPosition(payload: {
        department_id: string;
        name_ru: string;
        name_en: string;
        description_ru?: string | null;
        description_en?: string | null;
        default_agent_name?: string | null;
        is_department_head?: boolean;
        display_order?: number;
    }): Promise<Position> {
        return this.request<Position>('/positions', {
            method: 'POST',
            body: JSON.stringify(payload),
            silent: true,
        });
    }

    async updatePosition(id: string, payload: Partial<{
        department_id: string;
        name_ru: string;
        name_en: string;
        description_ru: string | null;
        description_en: string | null;
        default_agent_name: string | null;
        is_department_head: boolean;
        display_order: number;
    }>): Promise<Position> {
        return this.request<Position>(`/positions/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
            silent: true,
        });
    }

    async deactivatePosition(id: string): Promise<Position> {
        return this.request<Position>(`/positions/${id}/deactivate`, {
            method: 'POST',
            silent: true,
        });
    }

    async reactivatePosition(id: string): Promise<Position> {
        return this.request<Position>(`/positions/${id}/reactivate`, {
            method: 'POST',
            silent: true,
        });
    }

    async listUsers(filters?: {
        role?: string;
        hasEmployee?: boolean;
        includeInactive?: boolean;
        limit?: number;
        offset?: number;
    }): Promise<UserListRead[]> {
        const params = new URLSearchParams();
        if (filters?.role) params.set('role', filters.role);
        if (filters?.hasEmployee !== undefined) {
            params.set('has_employee', String(filters.hasEmployee));
        }
        if (filters?.includeInactive) params.set('include_inactive', 'true');
        if (filters?.limit) params.set('limit', String(filters.limit));
        if (filters?.offset) params.set('offset', String(filters.offset));
        const qs = params.toString() ? `?${params.toString()}` : '';
        return this.request<UserListRead[]>(`/users${qs}`);
    }

    async createEmployee(payload: {
        user_id: string;
        full_name_ru: string;
        full_name_en?: string | null;
        department_id: string;
        position_id?: string | null;
        manager_id?: string | null;
        contact_telegram?: string | null;
        contact_phone?: string | null;
        hired_at?: string | null;
    }): Promise<Employee> {
        return this.request<Employee>('/employees', {
            method: 'POST',
            body: JSON.stringify(payload),
            silent: true,
        });
    }

    async updateEmployee(id: string, payload: Partial<{
        full_name_ru: string;
        full_name_en: string | null;
        department_id: string;
        position_id: string | null;
        manager_id: string | null;
        contact_telegram: string | null;
        contact_phone: string | null;
        hired_at: string | null;
    }>): Promise<Employee> {
        return this.request<Employee>(`/employees/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
            silent: true,
        });
    }

    async deactivateEmployee(id: string): Promise<Employee> {
        return this.request<Employee>(`/employees/${id}/deactivate`, {
            method: 'POST',
            silent: true,
        });
    }

    async reactivateEmployee(id: string): Promise<Employee> {
        return this.request<Employee>(`/employees/${id}/reactivate`, {
            method: 'POST',
            silent: true,
        });
    }

    // ── Graph / Evidence Trust ────────────────────────────────────────────────

    async listClaims(filters?: { review_status?: string; page?: number; page_size?: number }): Promise<any[]> {
        const params = new URLSearchParams();
        if (filters?.review_status) params.set('review_status', filters.review_status);
        if (filters?.page) params.set('page', String(filters.page));
        if (filters?.page_size) params.set('page_size', String(filters.page_size));
        const qs = params.toString() ? `?${params.toString()}` : '';
        return this.request<any[]>(`/graph/claims${qs}`, { silent: true });
    }

    async listMissingEvidence(page: number = 1, page_size: number = 50): Promise<any[]> {
        return this.request<any[]>(`/graph/claims/missing-evidence?page=${page}&page_size=${page_size}`, { silent: true });
    }

    async promoteClaim(claimId: string, factStatement: string): Promise<any> {
        return this.request<any>(`/graph/claims/${claimId}/promote`, {
            method: 'POST',
            body: JSON.stringify({ fact_statement: factStatement }),
        });
    }

    // ── Literature HITL review (M5) ───────────────────────────────────────────

    async getLiteratureReviewQueue(params?: {
        review_status?: string;
        limit?: number;
        offset?: number;
        ks_node_id?: string;
    }): Promise<{ items: any[]; total: number }> {
        const qs = new URLSearchParams();
        if (params?.review_status) qs.set('review_status', params.review_status);
        if (params?.limit)         qs.set('limit',         String(params.limit));
        if (params?.offset)        qs.set('offset',        String(params.offset));
        if (params?.ks_node_id)    qs.set('ks_node_id',    params.ks_node_id);
        const q = qs.toString() ? `?${qs}` : '';
        return this.request<any>(`/knowledge-base/literature/review-queue${q}`, { silent: true });
    }

    async approveLiteratureClaim(claimId: string, factStatement?: string): Promise<any> {
        return this.request<any>(`/knowledge-base/literature/claims/${claimId}/approve`, {
            method: 'POST',
            body: JSON.stringify({ fact_statement: factStatement ?? null }),
        });
    }

    async rejectLiteratureClaim(claimId: string, reasonCode: string, reasonNotes?: string): Promise<any> {
        return this.request<any>(`/knowledge-base/literature/claims/${claimId}/reject`, {
            method: 'POST',
            body: JSON.stringify({ reason_code: reasonCode, reason_notes: reasonNotes ?? '' }),
        });
    }

    async editLiteratureClaim(claimId: string, fields: {
        claim_text?: string;
        qualifiers?: Record<string, unknown>;
        predicate?: string;
        object_value?: string;
        object_unit?: string;
    }): Promise<any> {
        return this.request<any>(`/knowledge-base/literature/claims/${claimId}/edit`, {
            method: 'PATCH',
            body: JSON.stringify(fields),
        });
    }

    async linkEntityToClaim(claimId: string, targetNodeId: string): Promise<any> {
        return this.request<any>(`/knowledge-base/literature/claims/${claimId}/link-entity`, {
            method: 'POST',
            body: JSON.stringify({ target_node_id: targetNodeId }),
        });
    }

    async unlinkEntityFromClaim(claimId: string, targetNodeId: string): Promise<any> {
        return this.request<any>(`/knowledge-base/literature/claims/${claimId}/unlink-entity`, {
            method: 'POST',
            body: JSON.stringify({ target_node_id: targetNodeId }),
        });
    }

    async getVerifiedClaims(limit = 50, offset = 0): Promise<{ items: any[]; total: number }> {
        return this.request<any>(
            `/knowledge-base/literature/verified-claims?limit=${limit}&offset=${offset}`,
            { silent: true }
        );
    }

    // ── Graph / Methods ───────────────────────────────────────────────────────

    async listMethods(): Promise<any[]> {
        return this.request<any[]>('/graph/methods', { silent: true });
    }

    async getMethod(methodId: string): Promise<any> {
        return this.request<any>(`/graph/methods/${methodId}`, { silent: true });
    }

    async listMeasurementsWithMethod(page: number = 1, page_size: number = 50): Promise<any[]> {
        return this.request<any[]>(`/graph/measurements?page=${page}&page_size=${page_size}`, { silent: true });
    }

    async getMeasurementMethod(measurementId: string): Promise<any | null> {
        return this.request<any>(`/graph/measurements/${measurementId}/method`, { silent: true });
    }

    async attachMethod(measurementId: string, methodNodeId: string): Promise<any> {
        return this.request<any>(`/graph/measurements/${measurementId}/method`, {
            method: 'POST',
            body: JSON.stringify({ method_node_id: methodNodeId }),
        });
    }

    // ── Graph / Equipment ─────────────────────────────────────────────────────

    async listEquipmentTypes(): Promise<any[]> {
        return this.request<any[]>('/graph/equipment-types', { silent: true });
    }

    async listEquipmentInstances(): Promise<any[]> {
        return this.request<any[]>('/graph/equipment-instances', { silent: true });
    }

    async getEquipmentInstance(instanceId: string): Promise<any> {
        return this.request<any>(`/graph/equipment-instances/${instanceId}`, { silent: true });
    }

    async attachCapability(instanceId: string, capabilityNodeId: string): Promise<any> {
        return this.request<any>(`/graph/equipment-instances/${instanceId}/capabilities`, {
            method: 'POST',
            body: JSON.stringify({ capability_node_id: capabilityNodeId }),
        });
    }

    // ── Graph / Process Routes ────────────────────────────────────────────────

    async listProcessRouteTemplates(): Promise<any[]> {
        return this.request<any[]>('/graph/process-route-templates', { silent: true });
    }

    async getProcessRouteTemplate(templateId: string): Promise<any> {
        return this.request<any>(`/graph/process-route-templates/${templateId}`, { silent: true });
    }

    async listProcessExecutions(): Promise<any[]> {
        return this.request<any[]>('/graph/process-executions', { silent: true });
    }

    async attachRequirement(templateId: string, requirementNodeId: string): Promise<any> {
        return this.request<any>(`/graph/process-route-templates/${templateId}/equipment-requirements`, {
            method: 'POST',
            body: JSON.stringify({ requirement_node_id: requirementNodeId }),
        });
    }

    // ── Graph / Governance Claims (Slice 4) ───────────────────────────────────

    async listLabelClaims(page = 1, page_size = 100): Promise<any[]> {
        return this.request<any[]>(`/graph/label-claims/?page=${page}&page_size=${page_size}`, { silent: true });
    }

    async getLabelClaim(nodeId: string): Promise<any> {
        return this.request<any>(`/graph/label-claims/${nodeId}`, { silent: true });
    }

    async listProductClaims(page = 1, page_size = 100): Promise<any[]> {
        return this.request<any[]>(`/graph/product-claims/?page=${page}&page_size=${page_size}`, { silent: true });
    }

    async listMarketingClaims(page = 1, page_size = 100): Promise<any[]> {
        return this.request<any[]>(`/graph/marketing-claims/?page=${page}&page_size=${page_size}`, { silent: true });
    }

    async attachFactToClaim(claimType: 'product-claims' | 'marketing-claims', nodeId: string, verifiedFactId: string): Promise<any> {
        return this.request<any>(`/graph/${claimType}/${nodeId}/fact`, {
            method: 'POST',
            body: JSON.stringify({ verified_fact_id: verifiedFactId }),
        });
    }

    async detachFactFromClaim(claimType: 'product-claims' | 'marketing-claims', nodeId: string, factId: string): Promise<void> {
        return this.request<void>(`/graph/${claimType}/${nodeId}/fact/${factId}`, { method: 'DELETE' });
    }

    // ── Graph / TestPlan (Slice 5) ────────────────────────────────────────────

    async listTestPlans(): Promise<any[]> {
        return this.request<any[]>('/graph/test-plans/', { silent: true });
    }

    async listExperimentDesigns(): Promise<any[]> {
        return this.request<any[]>('/graph/experiment-designs/', { silent: true });
    }

    async listExperimentRuns(): Promise<any[]> {
        return this.request<any[]>('/graph/experiment-runs/', { silent: true });
    }

    async getExperimentRun(runId: string): Promise<any> {
        return this.request<any>(`/graph/experiment-runs/${runId}`, { silent: true });
    }

    // ── Graph / Documents (Slice 6) ───────────────────────────────────────────

    async listDocumentTemplates(): Promise<any[]> {
        return this.request<any[]>('/graph/document-templates/', { silent: true });
    }

    async listGeneratedDocuments(): Promise<any[]> {
        return this.request<any[]>('/graph/generated-documents/', { silent: true });
    }

    async listApprovedDocuments(): Promise<any[]> {
        return this.request<any[]>('/graph/approved-documents/', { silent: true });
    }

    async getGeneratedDocument(nodeId: string): Promise<any> {
        return this.request<any>(`/graph/generated-documents/${nodeId}`, { silent: true });
    }
}

export const api = new ApiClient();
